/**
 * Tipos de dominio para el módulo de Límites del Backoffice Operativo.
 * Alineados con BackofficeLimitRuleController — /api/v1/bo/limit-rules.
 *
 * @taskId T478
 * @see functional/Specs/specs_cycle_4.md HU-28-FE
 */

// ─── Enums alineados con BE ──────────────────────────────────────────────────

export type LimitSubjectType = 'PF' | 'PJ' | 'ALL';
export type LimitOperationType = 'TRANSFER';
export type LimitPeriodScope = 'DAILY' | 'MONTHLY' | 'ANNUAL';

// ─── Entidades principales ──────────────────────────────────────────────────

/**
 * Regla de límite transaccional — alineada con LimitRuleResponse del BE.
 */
export interface LimitRule {
  id: string;
  tenantId: string;
  subjectType: LimitSubjectType;
  operationType: LimitOperationType;
  /** Período de aplicación */
  scope: LimitPeriodScope;
  /** Límite operativo PSP (BigDecimal como string) */
  pspLimit: string;
  /** Límite normativo (BigDecimal como string, opcional) */
  regulatoryLimit?: string;
  active: boolean;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt: string;
}

/**
 * Asignación de regla a merchant — alineada con LimitAssignmentResponse del BE.
 */
export interface LimitAssignment {
  id: string;
  tenantId: string;
  ruleId: string;
  merchantId: string;
  active: boolean;
  assignedBy: string;
  assignedAt: string;
  unassignedBy?: string;
  unassignedAt?: string;
}

// ─── View-model FE (tabla + modales) ────────────────────────────────────────

/**
 * View-model de regla de límite para el Backoffice (tabla + modales).
 * Usa nombres en español alineados con la UX definida en HU-28-FE.
 * Mapeado desde LimitRule al cargar datos del BE.
 */
export interface LimitRuleSummary {
  id: string;
  nombre: string;
  aplicaA: LimitSubjectType;
  /** Tipo de transacción (string amplio para soportar futuros valores post-MVP) */
  tipoTransaccion: string;
  periodo: LimitPeriodScope;
  monto: string;
  moneda: string;
  limiteNormativo?: string;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── T480 — tipos de asignación (pendiente implementación) ──────────────────

export interface MerchantAssignmentSummary {
  id: string;
  razonSocial: string;
  cuit: string;
  isAssigned: boolean;
  assignmentId?: string;
}

export interface AssignMerchantPayload {
  merchantId: string;
}

// ─── Request/Response types ─────────────────────────────────────────────────

export interface LimitRuleListParams {
  page?: number;
  size?: number;
  active?: boolean;
  operationType?: LimitOperationType;
}

export interface LimitRuleListResponse {
  items: LimitRule[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface CreateLimitRulePayload {
  subjectType: LimitSubjectType;
  operationType: LimitOperationType;
  scope: LimitPeriodScope;
  pspLimit: string;
  regulatoryLimit?: string;
  description?: string;
}

export interface UpdateLimitRulePayload {
  pspLimit?: string;
  regulatoryLimit?: string;
  description?: string;
  active?: boolean;
}

// ─── Labels y helpers ───────────────────────────────────────────────────────

export const SUBJECT_TYPE_LABELS: Record<LimitSubjectType, string> = {
  PF: 'Persona Física',
  PJ: 'Persona Jurídica',
  ALL: 'Todos',
};

export const OPERATION_TYPE_LABELS: Record<LimitOperationType, string> = {
  TRANSFER: 'Transferencia',
};

export const PERIOD_SCOPE_LABELS: Record<LimitPeriodScope, string> = {
  DAILY: 'Diario',
  MONTHLY: 'Mensual',
  ANNUAL: 'Anual',
};

// ─── Backward-compatible aliases (tests preexistentes T481) ─────────────────

/** @alias LimitSubjectType */
export type LimitScope = LimitSubjectType;

/** Período ampliado (incluye valores futuros post-MVP) */
export type LimitPeriod = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'PER_TRANSACTION';

/** Tipos de transacción ampliados (incluye valores futuros post-MVP) */
export type TransactionTypeLimit = 'TRANSFER_OUT' | 'TRANSFER_IN' | 'DEBIN' | 'ALL';

/** @alias SUBJECT_TYPE_LABELS */
export const SCOPE_LABELS: Record<LimitScope, string> = SUBJECT_TYPE_LABELS;

export const PERIOD_LABELS: Record<LimitPeriod, string> = {
  DAILY: 'Diario',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensual',
  YEARLY: 'Anual',
  PER_TRANSACTION: 'Por transacción',
};

export const TRANSACTION_TYPE_LABELS: Record<TransactionTypeLimit, string> = {
  TRANSFER_OUT: 'Transferencia saliente',
  TRANSFER_IN: 'Transferencia entrante',
  DEBIN: 'DEBIN',
  ALL: 'Todas',
};

/**
 * Genera un nombre descriptivo para la regla a partir de sus campos BE.
 */
export function formatRuleName(
  rule: Pick<LimitRule, 'subjectType' | 'operationType' | 'scope'>,
): string {
  return `${SUBJECT_TYPE_LABELS[rule.subjectType]} — ${OPERATION_TYPE_LABELS[rule.operationType]} — ${PERIOD_SCOPE_LABELS[rule.scope]}`;
}

/**
 * Formatea un monto ARS. Acepta undefined (campos opcionales del BE).
 */
export function formatMoney(amount: string | undefined): string {
  if (!amount) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
  }).format(num);
}

/**
 * Valida que el límite PSP sea > 0 y, si existe normativo, no lo supere.
 */
export function isValidLimit(pspLimit: string, regulatoryLimit?: string): boolean {
  const val = parseFloat(pspLimit);
  if (isNaN(val) || val <= 0) return false;
  if (regulatoryLimit !== undefined) {
    const reg = parseFloat(regulatoryLimit);
    if (!isNaN(reg) && val > reg) return false;
  }
  return true;
}

/**
 * Mapea una LimitRule (contrato BE) a LimitRuleSummary (view-model FE).
 */
export function toLimitRuleSummary(rule: LimitRule): LimitRuleSummary {
  return {
    id: rule.id,
    nombre: rule.description ?? formatRuleName(rule),
    aplicaA: rule.subjectType,
    tipoTransaccion: rule.operationType,
    periodo: rule.scope,
    monto: rule.pspLimit,
    moneda: 'ARS',
    limiteNormativo: rule.regulatoryLimit,
    activo: rule.active,
    createdAt: rule.createdAt,
    updatedAt: rule.updatedAt,
  };
}
